import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { createConsumer, Subscription } from '@rails/actioncable';
import { captureMessage } from '@sentry/react';
import {
  type Callbacks,
  type ChannelsContextType,
  type Data,
  type WebSocketError,
  WebSocketEvent,
  type WebSocketReceived,
  type WebSocketSuccess,
} from './types';
import { getAuthToken } from '@/utils/auth-token';

const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};
import { logWebSocketError } from './utils';

const isWebSocketError = (data: WebSocketReceived): data is WebSocketError =>
  'error' in data;

const isWebSocketSuccess = (
  data: WebSocketReceived
): data is WebSocketSuccess => 'event' in data;

// eslint-disable-next-line react-refresh/only-export-components
export const ChannelsContext = createContext<ChannelsContextType | null>(null);

export const ChannelsProvider = ({ children }: { children: ReactNode }) => {
  const actionCableRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const tokenRef = useRef<string | null>(null);

  const getOrCreateConsumer = useCallback(() => {
    const currentToken = getAuthToken();

    const shouldRecreateConsumer =
      actionCableRef.current == null ||
      (currentToken !== null && tokenRef.current !== currentToken);

    if (shouldRecreateConsumer) {
      if (actionCableRef.current) {
        actionCableRef.current.disconnect();
      }
      tokenRef.current = currentToken;
      const actionCableUrl = `${getWebSocketUrl()}/cable?token=${currentToken}`;
      actionCableRef.current = createConsumer(actionCableUrl);
    }

    return actionCableRef.current;
  }, []);

  const subscribe = useCallback(
    (data: Data, callbacks: Callbacks) => {
      const actionCable = getOrCreateConsumer();
      if (!actionCable) return;

      if (subscriptionRef.current) {
        actionCable.subscriptions.remove(subscriptionRef.current);
      }

      subscriptionRef.current = actionCable.subscriptions.create(data, {
        received: (receivedData: WebSocketReceived) => {
          if (isWebSocketError(receivedData)) {
            if (receivedData.message) {
              if (callbacks.onError) {
                callbacks.onError(receivedData);
              } else {
                logWebSocketError(data, receivedData);
                callbacks.received(receivedData);
              }
              return;
            }
          }

          const processingFinished =
            receivedData.event === WebSocketEvent.ProcessingFinished;

          if (isWebSocketSuccess(receivedData) && processingFinished) {
            callbacks.received(receivedData);
          }
        },
        rejected: () => {
          captureMessage(
            `WebSocket Error: [${data.resource_type} | ${data.resource_id}] Subscription rejected`,
            {
              level: 'error',
              tags: {
                component: 'websocket',
                resource_type: data.resource_type,
                action: 'subscription_rejected',
              },
              contexts: {
                websocket: {
                  channel: data.channel,
                  resource_type: data.resource_type,
                  resource_id: data.resource_id,
                  reason: 'Subscription rejected by server',
                },
              },
            }
          );
        },
      });
    },
    [getOrCreateConsumer]
  );

  const unsubscribe = useCallback(() => {
    const actionCable = actionCableRef.current;
    if (!actionCable) return;

    if (subscriptionRef.current) {
      actionCable.subscriptions.remove(subscriptionRef.current);
      subscriptionRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    const actionCable = actionCableRef.current;
    if (!actionCable) return;

    unsubscribe();
    actionCable.disconnect();
    actionCableRef.current = null;
  }, [unsubscribe]);

  return (
    <ChannelsContext.Provider value={{ subscribe, unsubscribe, disconnect }}>
      {children}
    </ChannelsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChannels = () => {
  const context = useContext(ChannelsContext);
  if (!context) {
    throw new Error('useChannels must be used within a ChannelsProvider');
  }
  return context;
};
